import * as React from 'react'
import { useFormikContext } from 'formik'
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Stack,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import ClearIcon from '@mui/icons-material/Clear'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

const SVG_MIN_LENGTH = 10

const isValidSvg = content => {
  if (!content || content.trim().length < SVG_MIN_LENGTH) return false
  const trimmed = content.trim()
  return (
    (trimmed.startsWith('<svg') || trimmed.includes('<svg ') || trimmed.includes('<svg\n')) &&
    trimmed.includes('</svg>')
  )
}

const SvgInputField = ({ name = 'icono_svg' }) => {
  const { values, setFieldValue, errors, touched } = useFormikContext()
  const value = values[name] || ''
  const fileInputRef = React.useRef(null)

  const hasError = Boolean(touched[name] && errors[name])
  const svgValid = isValidSvg(value)

  const handleFileUpload = e => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar extensión y MIME
    const isSvgExt = file.name.toLowerCase().endsWith('.svg')
    const isSvgMime = file.type === 'image/svg+xml' || file.type === ''

    if (!isSvgExt) {
      setFieldValue(name, '')
      alert('El archivo debe tener extensión .svg')
      e.target.value = ''
      return
    }

    const reader = new FileReader()
    reader.onload = event => {
      const content = event.target.result

      if (!isValidSvg(content)) {
        setFieldValue(name, '')
        alert('El archivo no contiene un SVG válido.')
        e.target.value = ''
        return
      }

      setFieldValue(name, content)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  const handleClear = () => {
    setFieldValue(name, '')
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="body2" color={hasError ? 'error' : 'text.primary'} fontWeight={500}>
          Ícono SVG *
        </Typography>

        {value && (
          svgValid ? (
            <CheckCircleOutlineIcon fontSize="small" color="success" />
          ) : (
            <ErrorOutlineIcon fontSize="small" color="error" />
          )
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".svg,image/svg+xml"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Subir archivo .svg
        </Button>

        {value && (
          <Tooltip title="Limpiar campo">
            <IconButton size="small" onClick={handleClear}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <TextField
        fullWidth
        multiline
        rows={4}
        variant="outlined"
        label="O pega el contenido SVG aquí"
        value={value}
        onChange={e => setFieldValue(name, e.target.value)}
        error={hasError}
        helperText={hasError ? errors[name] : 'Contenido SVG inline (obligatorio)'}
        InputLabelProps={{ shrink: true }}
        inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
      />

      {value && svgValid && (
        <Paper
          variant="outlined"
          sx={{
            mt: 1,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'grey.50',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Vista previa:
          </Typography>
          <Box
            sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            dangerouslySetInnerHTML={{ __html: value }}
          />
        </Paper>
      )}
    </Box>
  )
}

export default SvgInputField
